import {
  LEGAL_ADDRESS,
  LEGAL_NAME,
  LEGAL_PHONE,
} from '@/lib/legal/business';
import { CONSULTATION_SERVICE_SLUG } from '@/lib/constants/booking';
import AppImage from '../ui/AppImage';
import Icon from '../ui/AppIcon';
import Link from 'next/link';
import FooterSocialLinks from './FooterSocialLinks';

export default function Footer() {
  const footerLinks = {
    services: [
      { id: 'footer_natural_twists', label: 'Natural Hair Twists', href: '#services' },
      { id: 'footer_mini_twists', label: 'Mini Twists', href: '#services' },
      { id: 'footer_washing', label: 'Washing & Conditioning', href: '#services' },
      { id: 'footer_school', label: 'School Hairstyles', href: '#services' },
    ],
    about: [
      { id: 'footer_team', label: 'Our Team', href: '#team' },
      { id: 'footer_facilities', label: 'Facilities', href: '#facilities' },
      { id: 'footer_testimonials', label: 'Testimonials', href: '#testimonials' },
    ],
    resources: [
      { id: 'footer_news', label: 'Hair Care Tips', href: '#news' },
      { id: 'footer_faq', label: 'FAQ', href: '#faq' },
      { id: 'footer_contact', label: 'Contact Us', href: '#contact' },
    ],
  }

  return (
    <footer className="relative mt-24">
      {/* Top Layer - Dark Brown with Watermark */}
      <div className="bg-[rgb(var(--color-primary))] text-primary-foreground py-16 relative overflow-hidden">
        {/* Large Watermark Text */}
        <div className='absolute flex flex-col justify-center inset-0'>
          <div className="relative -top-20 inset-0 flex items-center justify-center opacity-5 pointer-events-none">
            <h2 className="text-[8rem] md:text-[10rem] lg:text-[12rem] font-bold tracking-wider">
              Mevoly HAIR
            </h2>
          </div>
          <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
            <h2 className="text-[8rem] md:text-[10rem] lg:text-[12rem] font-bold tracking-wider text-transparent stroke-text">
              Mevoly HAIR
            </h2>
          </div>
          <div className="relative inset-0 flex items-center justify-center pointer-events-none top-20">
            <h2 className="text-[8rem] md:text-[10rem] lg:text-[12rem] font-bold tracking-wider">
              Mevoly HAIR
            </h2>
          </div>
        </div>

        {/* Floating White Card */}
        <div className="container mx-auto px-6 relative z-10">
          <div className="bg-primary border-2 border-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Logo & Description */}
              <div className="lg:col-span-1">
                <div className="flex items-center gap-2 mb-4">
                  <AppImage
                    src="/logos/MEVOLY_white1.png"
                    alt="Mevoly Logo"
                    className="w-44 h-auto object-cover"
                    priority
                  />
                </div>
                <p className="text-sm text-white leading-relaxed mb-4">
                  Professional Hair Styling with Expert Care, Transforming Your Look with Confidence.
                </p>

                {/* Contact Info */}
                <div className="space-y-2 mb-6">
                  <div className="flex items-start gap-2 text-sm text-white">
                    <Icon name="MapPinIcon" size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{LEGAL_ADDRESS}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white">
                    <Icon name="PhoneIcon" size={16} />
                    <span>{LEGAL_PHONE}</span>
                  </div>
                </div>

                <FooterSocialLinks />
              </div>

              {/* Services Column */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">
                  Services
                </h3>
                <ul className="space-y-3">
                  <li>
                    <Link href="/services" className="text-sm text-white hover:text-white/75 transition-colors">
                      All Services
                    </Link>
                  </li>
                  <li>
                    <Link href="/services#pricing" className="text-sm text-white hover:text-white/75 transition-colors">
                      Pricing
                    </Link>
                  </li>
                  <li>
                    <Link href="/faq" className="text-sm text-white hover:text-white/75 transition-colors">
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={`/?book=1&step=1&services=${CONSULTATION_SERVICE_SLUG}`}
                      className="text-sm text-white hover:text-white/75 transition-colors"
                    >
                      Schedule a Consultation
                    </Link>
                  </li>
                </ul>
              </div>

              {/* About Column */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">
                  About Us
                </h3>
                <ul className="space-y-3">
                  <li>
                    <Link href="#story" className="text-sm text-white hover:text-white/75 transition-colors">
                      Our Story
                    </Link>
                  </li>
                  <li>
                    <Link href="#team" className="text-sm text-white hover:text-white/75 transition-colors">
                      Our Team
                    </Link>
                  </li>
                  <li>
                    <Link href="#careers" className="text-sm text-white hover:text-white/75 transition-colors">
                      Careers
                    </Link>
                  </li>
                  <li>
                    <Link href="#contact" className="text-sm text-white hover:text-white/75 transition-colors">
                      Contact Us
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Resources Column */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">
                  Resources
                </h3>
                <ul className="space-y-3">
                  <li>
                    <Link href="/insights" className="text-sm text-white hover:text-white/75 transition-colors">
                      Hair Care Tips
                    </Link>
                  </li>
                  <li>
                    <Link href="/privacy" className="text-sm text-white hover:text-white/75 transition-colors">
                      Privacy
                    </Link>
                  </li>
                  <li>
                    <Link href="/terms" className="text-sm text-white hover:text-white/75 transition-colors">
                      Terms
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-white">
                © {new Date().getFullYear()} {LEGAL_NAME}. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <Link href="/privacy" className="text-sm text-white hover:text-white/75 transition-colors">
                  Privacy Policy
                </Link>
                <Link href="/terms" className="text-sm text-white hover:text-white/75 transition-colors">
                  Terms & Conditions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}